import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

function sessionHref(s) {
  return s.type === "internal_1_1" ? `/app/team-1-1/${s.id}` : `/app/sessions/${s.id}`;
}

function TypeBadge({ type, t }) {
  if (type === "internal_1_1") {
    return <span className="tb-badge accent" data-testid="badge-1-1">👥 {t("team1v1.badge.internal_1_1")}</span>;
  }
  if (type === "mandate") {
    return <span className="tb-badge" data-testid="badge-mandate">📋 {t("team1v1.badge.mandate")}</span>;
  }
  return <span className="tb-badge" data-testid="badge-advisory">🎓 {t("team1v1.badge.advisory")}</span>;
}

function counterpartLabel(s, meId, t) {
  if (s.type === "internal_1_1") {
    const other = (s.participants || []).find((p) => p.id !== meId) || (s.participants || [])[0];
    return other?.name ? t("team1v1.withPerson", { name: other.name }) : "";
  }
  const names = [s.advisor?.name, s.entrepreneur?.name].filter(Boolean);
  return names.join(" · ");
}

function SessionCard({ s, lang, meId, t, past }) {
  const dt = new Date(s.scheduled_at);
  const dateStr = dt.toLocaleString(lang === "fr" ? "fr-CA" : "en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const is11 = s.type === "internal_1_1";
  let status;
  if (s.has_summary || s.has_team_summary) {
    status = <span className="tb-badge good">✓ {t("sessions.summaryReady")}</span>;
  } else if (past) {
    status = <span className="tb-badge warn">{t("sessions.recapPending")}</span>;
  } else if (is11) {
    status = <span className="tb-badge warn">{t("team1v1.agenda")}</span>;
  } else if (s.has_pre_form) {
    status = <span className="tb-badge accent">{t("sessions.preDone")}</span>;
  } else {
    status = <span className="tb-badge warn">{t("sessions.preMissing")}</span>;
  }
  return (
    <Link to={sessionHref(s)} className="tb-app-card" data-testid={`session-card-${s.id}`}>
      <span className="tb-when">{dateStr}</span>
      <strong>{s.title || (is11 ? t("team1v1.badge.internal_1_1") : t("team1v1.badge.advisory"))}</strong>
      <small>{counterpartLabel(s, meId, t)}</small>
      <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
        <TypeBadge type={s.type || "advisory"} t={t} />
        {status}
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const canBook = user.role === "entrepreneur" || user.role === "advisor" || user.role === "admin" || user.role === "accelerator";
  const canMandate = user.role === "accelerator";

  useEffect(() => {
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => setSessions([]));
  }, [user.role]); // eslint-disable-line react-hooks/exhaustive-deps

  if (sessions === null) {
    return <div className="tb-loading">{t("common.loading")}</div>;
  }

  const now = Date.now();
  const items = sessions
    .map((s) => ({ at: new Date(s.scheduled_at).getTime(), s }))
    .sort((a, b) => a.at - b.at);

  const upcoming = items.filter((x) => x.at > now);
  const past = items.filter((x) => x.at <= now).reverse();

  return (
    <div className="tb-page">
      <h1 className="tb-h1" data-testid="dashboard-heading">
        {t("nav.dashboard")}
      </h1>

      {canBook && (
        <div style={{ marginBottom: "1rem", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="tb-primary-btn"
            onClick={() => navigate("/app/sessions/new")}
            data-testid="cta-book-session"
          >
            {t("dash.book")}
          </button>
          {canMandate && (
            <button
              className="tb-ghost-btn"
              onClick={() => navigate("/app/mandates")}
              data-testid="cta-book-mandate"
            >
              {t("dash.bookMandate")}
            </button>
          )}
        </div>
      )}

      {(user.role === "provider" || user.role === "investor") && (
        <p className="tb-muted" style={{ marginBottom: "1.5rem" }}>
          {t("dash.observer")}
        </p>
      )}

      <h2 className="tb-h2" data-testid="heading-upcoming">
        {t("dash.upcoming")}
      </h2>
      {upcoming.length === 0 ? (
        <p className="tb-muted" data-testid="no-upcoming">{t("dash.noUpcoming")}</p>
      ) : (
        <div className="tb-card-grid" data-testid="upcoming-grid">
          {upcoming.map((x) => (
            <SessionCard key={x.s.id} s={x.s} lang={lang} meId={user.id} t={t} past={false} />
          ))}
        </div>
      )}

      <h2 className="tb-h2" data-testid="heading-past">
        {t("dash.past")}
      </h2>
      {past.length === 0 ? (
        <p className="tb-muted" data-testid="no-past">{t("dash.noPast")}</p>
      ) : (
        <div className="tb-card-grid" data-testid="past-grid">
          {past.map((x) => (
            <SessionCard key={x.s.id} s={x.s} lang={lang} meId={user.id} t={t} past />
          ))}
        </div>
      )}
    </div>
  );
}
